import { ApiFetch } from '../../../shared/utils/fetch';
import { unwrap, type Enveloped } from '../../../shared/utils/unwrap';

/**
 * Branch / location management (PE-2139, on the PE-2138 backend).
 *
 * ⚠️ SCOPING — branches are NOT x-tenant-id scoped. Watchman's branches controller
 * (proxied by osprey-api `@Controller('branches')`) scopes by an explicit `merchantId`:
 *   - MERCHANT-scoped callers  → merchantId is forced server-side and ignored if sent.
 *   - COLLECTIVE-scoped callers (i.e. Osprey TCS staff, which is us) → MUST pass merchantId,
 *     as a query param on reads and in the body on create.
 * That is why `/branches` is deliberately absent from TENANT_SCOPED_PREFIXES in
 * shared/utils/fetch.ts — adding it there would send a header the route ignores while
 * still omitting the param it actually requires.
 *
 * Field constraints mirror watchman's Create/UpdateBranchDto:
 *   name        required, 2–255 chars
 *   description optional, ≤1000 chars
 *   address     optional, ≤1000 chars
 * `isActive` is never set through create/update — it moves only via the
 * activate/deactivate routes below (a branch is deactivated, not hard-deleted).
 */

export const BRANCH_NAME_MIN = 2;
export const BRANCH_NAME_MAX = 255;
export const BRANCH_TEXT_MAX = 1000;

/** Mirrors watchman's `Branch` prisma model. */
export interface Branch {
  id: string;
  merchantId: string;
  name: string;
  description: string | null;
  address: string | null;
  /** Defaults to true server-side; toggled only via activate/deactivate. */
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateBranchPayload {
  name: string;
  description?: string;
  address?: string;
  /** Required for us (COLLECTIVE-scoped); ignored for merchant-scoped callers. */
  merchantId?: string;
}

export type UpdateBranchPayload = Partial<Omit<CreateBranchPayload, 'merchantId'>>;

export const branchesApi = {
  list: async (merchantId: string): Promise<Branch[]> => {
    const qs = new URLSearchParams({ merchantId });
    const response = await ApiFetch.get<Enveloped<Branch[]>>(`/branches?${qs.toString()}`);
    return unwrap(response);
  },

  get: async (id: string): Promise<Branch> => {
    const response = await ApiFetch.get<Enveloped<Branch>>(`/branches/${id}`);
    return unwrap(response);
  },

  create: async (data: CreateBranchPayload): Promise<Branch> => {
    const response = await ApiFetch.post<Enveloped<Branch>>('/branches', data);
    return unwrap(response);
  },

  update: async (id: string, data: UpdateBranchPayload): Promise<Branch> => {
    // ApiFetch.put issues a PATCH on the wire (see shared/utils/fetch.ts) → PATCH /branches/:id.
    const response = await ApiFetch.put<Enveloped<Branch>>(`/branches/${id}`, data);
    return unwrap(response);
  },

  /** Soft-remove. There is no DELETE route — removal is a deactivate. */
  deactivate: async (id: string): Promise<Branch> => {
    const response = await ApiFetch.put<Enveloped<Branch>>(`/branches/${id}/deactivate`);
    return unwrap(response);
  },

  activate: async (id: string): Promise<Branch> => {
    const response = await ApiFetch.put<Enveloped<Branch>>(`/branches/${id}/activate`);
    return unwrap(response);
  },
};

/**
 * Per-entity branch availability (PE-2139 second AC).
 *
 * The backend models this as EXCLUSIONS, not inclusions: an entity is available at
 * every branch by default, and a row here means "not available at this branch".
 * That inverts the UI — a ticked branch checkbox means *no* exclusion row exists.
 *
 * The same three verbs exist for each entity type, so one shared client covers all of them.
 */
export const BRANCH_EXCLUSION_ENTITIES = {
  product: 'products',
  // ⚠️ NOT 'bookings/services'. osprey-api #95/#97 moved only the booking-service
  // exclusion routes off the /bookings/services collection onto their own
  // /booking-services base; the collection itself (GET /bookings/services) kept
  // its old path. The two segments disagree on purpose — do not "tidy" this to
  // match the sibling keys.
  bookingService: 'booking-services',
  packageListing: 'packages/listings',
} as const;

export type BranchExclusionEntity = keyof typeof BRANCH_EXCLUSION_ENTITIES;

export const branchExclusionsApi = {
  /** Branch ids at which this entity is NOT available. */
  list: async (entity: BranchExclusionEntity, entityId: string): Promise<string[]> => {
    const base = BRANCH_EXCLUSION_ENTITIES[entity];
    const response = await ApiFetch.get<Enveloped<unknown>>(`/${base}/${entityId}/branch-exclusions`);
    const raw = unwrap(response);
    // The BFF documents no response schema for these routes, and the three owners disagree
    // on shape, so accept all of them:
    //   • bare id strings                      — product-service returns string[]
    //   • { id, name }                         — osprey-api's enrichBranchExclusions wraps
    //                                            product-service's ids with the branch name
    //   • { branchId }                         — row-shaped, for any owner that returns rows
    // ⚠️ PE-2273 bug 5: reading only `branchId` silently dropped every { id, name } row, so the
    // list came back empty, every branch looked un-excluded and all toggles rendered ON even
    // straight after a successful exclude. Keep `id` in this list.
    if (!Array.isArray(raw)) return [];
    return raw
      .map((row) => {
        if (typeof row === 'string') return row;
        const r = row as { branchId?: string; id?: string } | null;
        return r?.branchId ?? r?.id;
      })
      .filter((id): id is string => typeof id === 'string');
  },

  /** Make the entity unavailable at this branch. */
  exclude: async (entity: BranchExclusionEntity, entityId: string, branchId: string): Promise<void> => {
    const base = BRANCH_EXCLUSION_ENTITIES[entity];
    await ApiFetch.post(`/${base}/${entityId}/branch-exclusions/${branchId}`);
  },

  /** Re-include the entity at this branch. */
  include: async (entity: BranchExclusionEntity, entityId: string, branchId: string): Promise<void> => {
    const base = BRANCH_EXCLUSION_ENTITIES[entity];
    await ApiFetch.del(`/${base}/${entityId}/branch-exclusions/${branchId}`);
  },
};
